import {
  formatPhoneForDisplay,
  normalizePaymentPhoneNumber,
  toInternationalDigits,
} from "@/lib/phone-number";

/**
 * Public contact details — phone, email, website, and social handles — shared
 * by hiives, venues, and events.
 *
 * All three own the same shape so a venue or an event can copy its hiive's
 * details in one tap. Phone / email / website live in their own columns
 * (venue had them first); the social handles live in a single JSON column so
 * adding a platform never needs a migration.
 *
 * Values are stored as the operator typed them (a handle or a full URL), except
 * phone numbers — the contact phone and a WhatsApp number — which are stored
 * as E.164. Rendering resolves them to an href at read time via
 * `socialLinkHref`, so a stored `@hiive` and a stored
 * `https://instagram.com/hiive` behave identically.
 */

export const SOCIAL_PLATFORM_IDS = [
  "instagram",
  "whatsapp",
  "facebook",
  "x",
  "youtube",
  "linkedin",
  "threads",
  "tiktok",
  "telegram",
] as const;

export type SocialPlatformId = (typeof SOCIAL_PLATFORM_IDS)[number];

export type SocialLinks = Partial<Record<SocialPlatformId, string>>;

type SocialPlatformMeta = {
  id: SocialPlatformId;
  label: string;
  placeholder: string;
  /** Prefixed to a bare handle to build the outbound href. */
  profileBase: string;
  /**
   * `handle` fields accept `@name` or `name`; `phone` fields accept a phone
   * number and resolve to a wa.me / t.me style link.
   */
  kind: "handle" | "phone";
};

export const SOCIAL_PLATFORMS: readonly SocialPlatformMeta[] = [
  {
    id: "instagram",
    label: "Instagram",
    placeholder: "@yourhandle",
    profileBase: "https://instagram.com/",
    kind: "handle",
  },
  {
    id: "whatsapp",
    label: "WhatsApp",
    placeholder: "+91 98765 43210",
    profileBase: "https://wa.me/",
    kind: "phone",
  },
  {
    id: "facebook",
    label: "Facebook",
    placeholder: "yourpage",
    profileBase: "https://facebook.com/",
    kind: "handle",
  },
  {
    id: "x",
    label: "X",
    placeholder: "@yourhandle",
    profileBase: "https://x.com/",
    kind: "handle",
  },
  {
    id: "youtube",
    label: "YouTube",
    placeholder: "@yourchannel",
    profileBase: "https://youtube.com/",
    kind: "handle",
  },
  {
    id: "linkedin",
    label: "LinkedIn",
    placeholder: "company/yourbrand",
    profileBase: "https://linkedin.com/",
    kind: "handle",
  },
  {
    id: "threads",
    label: "Threads",
    placeholder: "@yourhandle",
    profileBase: "https://threads.net/",
    kind: "handle",
  },
  {
    id: "tiktok",
    label: "TikTok",
    placeholder: "@yourhandle",
    profileBase: "https://tiktok.com/",
    kind: "handle",
  },
  {
    id: "telegram",
    label: "Telegram",
    placeholder: "@yourchannel",
    profileBase: "https://t.me/",
    kind: "handle",
  },
] as const;

const PLATFORM_BY_ID = new Map<SocialPlatformId, SocialPlatformMeta>(
  SOCIAL_PLATFORMS.map((platform) => [platform.id, platform]),
);

export function getSocialPlatform(
  id: SocialPlatformId,
): SocialPlatformMeta | undefined {
  return PLATFORM_BY_ID.get(id);
}

export function isSocialPlatformId(value: string): value is SocialPlatformId {
  return PLATFORM_BY_ID.has(value as SocialPlatformId);
}

/** Max stored length for one handle — generous enough for a full profile URL. */
export const SOCIAL_LINK_MAX_LENGTH = 300;

/** Hosts each handle platform's profile links live on, minus `www.`/`m.`. */
const PLATFORM_HOSTS: Partial<Record<SocialPlatformId, readonly string[]>> = {
  instagram: ["instagram.com", "instagr.am"],
  facebook: ["facebook.com", "fb.com"],
  x: ["x.com", "twitter.com"],
  youtube: ["youtube.com"],
  linkedin: ["linkedin.com"],
  threads: ["threads.net", "threads.com"],
  tiktok: ["tiktok.com"],
  telegram: ["t.me", "telegram.me"],
};

/** First path segments that are a platform's own pages, not a profile. */
const NON_PROFILE_SEGMENTS: Partial<
  Record<SocialPlatformId, ReadonlySet<string>>
> = {
  instagram: new Set(["p", "reel", "reels", "tv", "explore", "accounts"]),
  facebook: new Set([
    "groups",
    "events",
    "share",
    "watch",
    "pages",
    "people",
    "profile.php",
  ]),
  x: new Set(["i", "intent", "home", "search", "hashtag"]),
  telegram: new Set(["joinchat", "addstickers", "s"]),
};

/**
 * What to store for a pasted handle or profile link: just the handle.
 *
 * "Copy link" in Instagram and friends appends tracking (`?igsh=…`,
 * `?utm_source=…`, `?stkn=…`), and people paste the whole thing. A profile URL
 * on the platform's own host becomes its bare handle (`amykxo`); any other
 * link keeps its address without the query or fragment. Facebook's numeric
 * `profile.php?id=` is the one query that IS the profile, so it survives.
 */
export function cleanSocialLinkValue(
  id: SocialPlatformId,
  value: string,
): string {
  const trimmed = value.trim();
  const platform = PLATFORM_BY_ID.get(id);
  if (!platform || platform.kind === "phone" || trimmed.length === 0)
    return trimmed;

  const looksLikeUrl =
    ABSOLUTE_URL.test(trimmed) ||
    (trimmed.includes(".") && trimmed.includes("/"));
  if (!looksLikeUrl) {
    // A bare handle, possibly with tracking glued on ("amykxo?igsh=…").
    return trimmed
      .replace(/[?#].*$/, "")
      .replace(/^@+/, "")
      .trim();
  }

  let url: URL;
  try {
    url = new URL(ABSOLUTE_URL.test(trimmed) ? trimmed : `https://${trimmed}`);
  } catch {
    return trimmed;
  }
  const host = url.hostname.toLowerCase().replace(/^(www|m|mobile)\./, "");
  const segments = url.pathname.split("/").filter(Boolean);
  const first = segments[0]?.toLowerCase();

  if (
    id === "facebook" &&
    first === "profile.php" &&
    PLATFORM_HOSTS.facebook?.includes(host)
  ) {
    const profileId = url.searchParams.get("id");
    if (profileId)
      return `https://facebook.com/profile.php?id=${encodeURIComponent(profileId)}`;
  }

  if (PLATFORM_HOSTS[id]?.includes(host) && first) {
    // instagram.com/stories/<handle>/… is still that person.
    if (id === "instagram" && first === "stories" && segments[1])
      return segments[1].replace(/^@+/, "");
    // linkedin.com/in/<name> and /company/<name> are the profile.
    if (
      id === "linkedin" &&
      segments[1] &&
      ["in", "company", "school"].includes(first)
    )
      return `${first}/${segments[1]}`;
    const isHandle =
      id === "youtube"
        ? first.startsWith("@")
        : id !== "linkedin" &&
          !NON_PROFILE_SEGMENTS[id]?.has(first) &&
          !first.startsWith("+");
    if (isHandle) return segments[0].replace(/^@+/, "");
  }

  const path = url.pathname.replace(/\/+$/, "");
  return `https://${url.host}${path}`;
}

/**
 * Drop empty/whitespace values and unknown platform keys, trim what survives
 * and strip pasted tracking down to the handle (`cleanSocialLinkValue`).
 * Returns a plain object so it can go straight into a JSON column.
 */
export function normalizeSocialLinks(
  input: Record<string, unknown> | null | undefined,
): SocialLinks {
  if (!input || typeof input !== "object") return {};

  const normalized: SocialLinks = {};
  for (const platform of SOCIAL_PLATFORMS) {
    const raw = (input as Record<string, unknown>)[platform.id];
    if (typeof raw !== "string") continue;
    const cleaned = cleanSocialLinkValue(platform.id, raw);
    if (cleaned.length === 0) continue;
    normalized[platform.id] = cleaned.slice(0, SOCIAL_LINK_MAX_LENGTH);
  }
  return normalized;
}

export function hasAnySocialLink(links: SocialLinks | null | undefined) {
  return links != null && Object.keys(normalizeSocialLinks(links)).length > 0;
}

const ABSOLUTE_URL = /^https?:\/\//i;

/**
 * WhatsApp's stored form: a number becomes E.164; anything else (a pasted
 * group invite or wa.me link) is kept as typed.
 */
export function normalizeWhatsAppLinkValue(value: string): string {
  const trimmed = value.trim();
  if (ABSOLUTE_URL.test(trimmed)) return trimmed;
  return normalizePaymentPhoneNumber(trimmed) ?? trimmed;
}

/**
 * Resolve a stored handle to an outbound URL. Returns null when the value
 * cannot be turned into a link, so callers can skip rendering it entirely.
 */
export function socialLinkHref(
  id: SocialPlatformId,
  value: string | null | undefined,
): string | null {
  const platform = PLATFORM_BY_ID.get(id);
  if (!platform) return null;

  // Values saved before tracking was stripped on save still carry it.
  const trimmed = cleanSocialLinkValue(id, value ?? "");
  if (trimmed.length === 0) return null;

  if (ABSOLUTE_URL.test(trimmed)) return trimmed;

  if (platform.kind === "phone") {
    // wa.me wants digits WITH the country code; a bare "98201 40079" gets 91.
    const dialable = toInternationalDigits(trimmed);
    return dialable ? `${platform.profileBase}${dialable}` : null;
  }

  // A bare domain the operator pasted without a scheme ("instagram.com/x").
  if (trimmed.includes("/") && trimmed.includes(".")) {
    return `https://${trimmed.replace(/^\/+/, "")}`;
  }

  const handle = trimmed.replace(/^@+/, "").replace(/^\/+/, "");
  if (handle.length === 0) return null;
  return `${platform.profileBase}${handle}`;
}

/** Short, human label for a stored handle — "@hiive" rather than a raw URL. */
export function socialLinkDisplay(
  id: SocialPlatformId,
  value: string | null | undefined,
): string {
  const platform = PLATFORM_BY_ID.get(id);
  const trimmed = cleanSocialLinkValue(id, value ?? "");
  if (!platform || trimmed.length === 0) return trimmed;

  if (platform.kind === "phone") {
    return ABSOLUTE_URL.test(trimmed)
      ? trimmed
      : (formatPhoneForDisplay(trimmed) ?? trimmed);
  }

  if (ABSOLUTE_URL.test(trimmed) || trimmed.includes("/")) {
    const withoutScheme = trimmed.replace(ABSOLUTE_URL, "").replace(/\/+$/, "");
    const lastSegment = withoutScheme.split("/").filter(Boolean).pop();
    return lastSegment ? `@${lastSegment.replace(/^@+/, "")}` : withoutScheme;
  }

  return `@${trimmed.replace(/^@+/, "")}`;
}

/** Normalize a bare website entry ("hiive.club") into an absolute URL. */
export function websiteHref(value: string | null | undefined): string | null {
  const trimmed = value?.trim() ?? "";
  if (trimmed.length === 0) return null;
  if (ABSOLUTE_URL.test(trimmed)) return trimmed;
  if (!trimmed.includes(".")) return null;
  return `https://${trimmed.replace(/^\/+/, "")}`;
}

/** Strip the scheme for display — "hiive.club" rather than "https://hiive.club/". */
export function websiteDisplay(value: string | null | undefined): string {
  const trimmed = value?.trim() ?? "";
  return trimmed.replace(ABSOLUTE_URL, "").replace(/\/+$/, "");
}

/**
 * The full public contact record, as stored. Hiives, venues, and events all
 * expose this exact shape.
 */
export type ContactDetails = {
  contactPhone: string | null;
  contactEmail: string | null;
  websiteUrl: string | null;
  socialLinks: SocialLinks;
};

/** Form-side mirror: never null, so inputs stay controlled. */
export type ContactDetailsFormValue = {
  contactPhone: string;
  contactEmail: string;
  websiteUrl: string;
  socialLinks: SocialLinks;
};

export const EMPTY_CONTACT_DETAILS_FORM_VALUE: ContactDetailsFormValue = {
  contactPhone: "",
  contactEmail: "",
  websiteUrl: "",
  socialLinks: {},
};

export function toContactDetailsFormValue(
  details: Partial<ContactDetails> | null | undefined,
): ContactDetailsFormValue {
  return {
    contactPhone: details?.contactPhone ?? "",
    contactEmail: details?.contactEmail ?? "",
    websiteUrl: details?.websiteUrl ?? "",
    socialLinks: { ...normalizeSocialLinks(details?.socialLinks) },
  };
}

export function hasAnyContactDetail(
  details: Partial<ContactDetails> | null | undefined,
) {
  if (!details) return false;
  return Boolean(
    details.contactPhone?.trim() ||
      details.contactEmail?.trim() ||
      details.websiteUrl?.trim() ||
      hasAnySocialLink(details.socialLinks),
  );
}
